import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import type { BffCatalogProductsResponse } from '@/core/models/bff-catalog';
import {
  bffCatalogProductsQueryKey,
  type BffCatalogProductsQueryParams,
  isBffCatalogEnabledForLocale,
  localeToBffCatalogParams,
} from '@/core/queries/bff/catalog/bff-catalog-query-keys';
import { getBffCatalogProducts } from '@/core/services/fetchers';

export type UseBffCatalogProductsQueryOptions = {
  /** Omit or `true` to request stock/availability; pass `false` to skip (lighter payload). */
  includeAvailability?: boolean;
};

export async function fetchBffCatalogProducts(
  params: BffCatalogProductsQueryParams,
): Promise<BffCatalogProductsResponse> {
  return getBffCatalogProducts({
    countryCode: params.countryCode,
    languageCode: params.languageCode,
    includeAvailability: params.includeAvailability,
  });
}

const useBffCatalogProductsQuery = (
  options: UseBffCatalogProductsQueryOptions = {},
  queryOptions?: Omit<
    UseQueryOptions<BffCatalogProductsResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const locale = useLocale();
  const { countryCode, languageCode } = localeToBffCatalogParams(locale);
  const includeAvailability = options.includeAvailability ?? true;
  const params: BffCatalogProductsQueryParams = {
    countryCode,
    languageCode,
    includeAvailability,
  };

  const isCatalogEnabled = isBffCatalogEnabledForLocale(locale);

  return useQuery({
    ...queryOptions,
    queryKey: bffCatalogProductsQueryKey(params),
    queryFn: () => fetchBffCatalogProducts(params),
    enabled:
      isCatalogEnabled && (queryOptions?.enabled ?? true),
  });
};

export default useBffCatalogProductsQuery;
